import { useEffect, useMemo, useState } from "react";
import { PageHeading } from "../../components/common/pageHeading/PageHeading";
import styles from "./reporting.module.css";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { faCalendarCheck } from "@fortawesome/free-solid-svg-icons";
import {
  useGetCurrMonthReportQuery,
  useGetLastMonthReportQuery,
  useGetThreeMonthsReportQuery,
} from "../../features/api/breakDown/breakDownApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SalesVsCollectionBarChart } from "../../components/salesVsCollectionBarChart/SalesVsCollectionBarChart";
import { ListFilter } from "../../components/common/listFilter/ListFilter";
import { useDispatch, useSelector } from "react-redux";
import { useGetAllUserQuery } from "../../features/api/user/userApi";
import {
  useGetCurrMonthWeeklyChartDataQuery,
  useGetLastMonthWeeklyChartDataQuery,
  useGetLastThreeMonthChartDataQuery,
} from "../../features/api/salesChartData/salesChratDataApi";
import { useIsSalesExecutive } from "../../customHooks/roleChecker/useIsSalesExecutive";
import { useFullName } from "../../customHooks/useFullName";
import { setFilter } from "../../features/filter/commonFilterSlice";
import { useIsSeniorSalesExecutive } from "../../customHooks/roleChecker/useIsSeniorSalesExecutive";
export const Reporting = () => {
  const isSalesExecutive = useIsSalesExecutive();
  const isSeniorSalesExecutive = useIsSeniorSalesExecutive();
  const dispatch = useDispatch();
  const userFullName = useFullName();
  const userInfo = useSelector((state) => state.user.info);
  const [activeKpi, setActiveKpi] = useState("current");
  const [chartDataKey, setChartDataKey] = useState("week");
  const [breakDown, setBreakDown] = useState({
    total: 0,
    due: 0,
    collection: 0,
    bankingTotal: 0,
    bankingCollection: 0,
    cashTotal: 0,
    cashCollection: 0,
  });
  const [barChartData, setBarChartData] = useState();
  const loader = <div className={styles.loaderImg}></div>;

  //current month data -
  const filterByExecutive = useSelector((state) => state.commonFilter);

  const { data: currMonthData, isLoading: isCurrLoading } =
    useGetCurrMonthReportQuery(filterByExecutive);

  // last month (skip unless active)
  const { data: lastMonthData, isLoading: isLastLoading } =
    useGetLastMonthReportQuery(filterByExecutive, {
      skip: activeKpi !== "last",
    });

  // three months (skip unless active)
  const { data: threeMonthData, isLoading: isThreeLoading } =
    useGetThreeMonthsReportQuery(filterByExecutive, {
      skip: activeKpi !== "three",
    });

  // when current month loads, fill breakdown by default
  useEffect(() => {
    if (currMonthData && activeKpi === "current") {
      setBreakDown({
        total: currMonthData.grandTotalAmount,
        collection:
          currMonthData.totalDeliveredCollectedAmount +
          currMonthData.totalInvoicedCollectedAmount,
        bankingTotal: currMonthData.totalInvoicedAmount,
        bankingCollection: currMonthData.totalInvoicedCollectedAmount,
        cashTotal: currMonthData.totalDeliveredAmount,
        cashCollection: currMonthData.totalDeliveredCollectedAmount,
      });
    }
  }, [currMonthData, activeKpi]);

  // when last month loads
  useEffect(() => {
    if (lastMonthData && activeKpi === "last") {
      setBreakDown({
        total: lastMonthData.grandTotalAmount,

        collection:
          lastMonthData.totalDeliveredCollectedAmount +
          lastMonthData.totalInvoicedCollectedAmount,
        bankingTotal: lastMonthData.totalInvoicedAmount,
        bankingCollection: lastMonthData.totalInvoicedCollectedAmount,
        cashTotal: lastMonthData.totalDeliveredAmount,
        cashCollection: lastMonthData.totalDeliveredCollectedAmount,
      });
    }
  }, [lastMonthData, activeKpi]);

  // when three months loads
  useEffect(() => {
    if (threeMonthData && activeKpi === "three") {
      setBreakDown({
        total: threeMonthData.grandTotalAmount,

        collection:
          threeMonthData.totalDeliveredCollectedAmount +
          threeMonthData.totalInvoicedCollectedAmount,
        bankingTotal: threeMonthData.totalInvoicedAmount,
        bankingCollection: threeMonthData.totalInvoicedCollectedAmount,
        cashTotal: threeMonthData.totalDeliveredAmount,
        cashCollection: threeMonthData.totalDeliveredCollectedAmount,
      });
    }
  }, [threeMonthData, activeKpi]);

  const isLoading =
    (activeKpi === "current" && isCurrLoading) ||
    (activeKpi === "last" && isLastLoading) ||
    (activeKpi === "three" && isThreeLoading);

  //Chart stat data -
  const {
    data: weeklySalesChartData,
    isLoading: weeklySalesChartDataLoading,
    isError: weeklySalesChartDataErr,
  } = useGetCurrMonthWeeklyChartDataQuery(filterByExecutive);

  const {
    data: lastMonthWeeklySalesChartData,
    isLoading: lastMonthWeeklySalesChartDataLoading,
    isError: lastMonthWeeklySalesChartDataErr,
  } = useGetLastMonthWeeklyChartDataQuery(filterByExecutive, {
    skip: activeKpi !== "last",
  });
  const {
    data: lastThreeMonthChartData,
    isLoading: isLastThreeMonthChartDataLoading,
    isError: isLastThreeMonthChartDataError,
  } = useGetLastThreeMonthChartDataQuery(filterByExecutive, {
    skip: activeKpi !== "three",
  });
  // For current month chart
  useEffect(() => {
    if (
      weeklySalesChartData &&
      Array.isArray(weeklySalesChartData) &&
      activeKpi === "current"
    ) {
      const formattedData = weeklySalesChartData.map((item) => ({
        week: `Week ${item.weekNumber}`,
        sales: item.sales,
        collection: item.collection,
      }));
      setBarChartData(formattedData);
      setChartDataKey("week"); // set dataKey to week
    }
  }, [weeklySalesChartData, activeKpi]);

  // For last month chart
  useEffect(() => {
    if (
      lastMonthWeeklySalesChartData &&
      Array.isArray(lastMonthWeeklySalesChartData) &&
      activeKpi === "last"
    ) {
      const formattedData = lastMonthWeeklySalesChartData.map((item) => ({
        week: `Week ${item.weekNumber}`,
        sales: item.sales,
        collection: item.collection,
      }));
      setBarChartData(formattedData);
      setChartDataKey("week"); // set dataKey to week
    }
  }, [lastMonthWeeklySalesChartData, activeKpi]);

  // For last three month chart
  useEffect(() => {
    if (
      lastThreeMonthChartData &&
      Array.isArray(lastThreeMonthChartData) &&
      activeKpi === "three"
    ) {
      const monthNames = [
        "",
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const formattedData = lastThreeMonthChartData.map((item) => ({
        month: monthNames[item.monthNumber],
        sales: item.sales,
        collection: item.collection,
      }));

      setBarChartData(formattedData);
      setChartDataKey("month"); // set dataKey to month
    }
  }, [lastThreeMonthChartData, activeKpi]);
  // Filter -
  const {
    data: createdByData,
    isLoading: isCreatedByListLoading,
    isError: createdByDataError,
  } = useGetAllUserQuery();

  // Build Order By Options dynamically
  // Build Order By Options dynamically
  const orderByOptions = useMemo(() => {
    if (!createdByData || !Array.isArray(createdByData)) return [];

    // If logged-in user is sales executive: show ONLY his own name
    let filteredUsers = createdByData;

    if (isSalesExecutive || isSeniorSalesExecutive) {
      filteredUsers = createdByData.filter(
        (u) =>
          `${u.firstName} ${u.lastName}` ===
          `${userInfo?.firstName} ${userInfo?.lastName}`,
      );
    }

    const options = filteredUsers.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: `${user.firstName} ${user.lastName}`,
    }));

    return [{ label: "All", value: "" }, ...options];
  }, [createdByData, isSalesExecutive, isSeniorSalesExecutive, userInfo]);
  useEffect(() => {
    if (isSalesExecutive || isSeniorSalesExecutive) {
      dispatch(
        setFilter({
          property: "createdBy",
          value: userFullName,
        }),
      );
    }
  }, [isSalesExecutive, userFullName]);
  return (
    <div className="mb-5">
      <PageHeading title={"Breakdown"} />
      <section className="container py-2">
        <div className="row align-items-center">
          <div className="py-2">
            {!isCreatedByListLoading &&
              !isSalesExecutive &&
              !isSeniorSalesExecutive && (
                <ListFilter
                  label={"Order By"}
                  optionList={orderByOptions}
                  filterName={"createdBy"}
                />
              )}
          </div>
          <div className="col-lg-3 col-md-3 col-sm-12 text-center">
            <h5 className="border">KPIs</h5>
            <div
              onClick={() => setActiveKpi("current")}
              className={`${styles.kpiMonth} ${
                activeKpi === "current" ? styles.kpiMonthActive : ""
              } col-12 text-center border mb-1 py-4`}
            >
              <FontAwesomeIcon
                icon={faCalendar}
                className={`${styles.amountIcon} ${styles.iconBankTotal}`}
              />
              <h6>Current Month</h6>
            </div>
            <div
              onClick={() => setActiveKpi("last")}
              className={`${styles.kpiMonth} ${
                activeKpi === "last" ? styles.kpiMonthActive : ""
              } col-12 text-center border mb-1 py-4`}
            >
              <FontAwesomeIcon
                icon={faCalendarCheck}
                className={`${styles.amountIcon} ${styles.iconBankTotal}`}
              />
              <h6>Last Month</h6>
            </div>
            <div
              onClick={() => setActiveKpi("three")}
              className={`${styles.kpiMonth} ${
                activeKpi === "three" ? styles.kpiMonthActive : ""
              } col-12 text-center border mb-1 py-4`}
            >
              <FontAwesomeIcon
                icon={faCalendarDays}
                className={`${styles.amountIcon} ${styles.iconBankTotal}`}
              />
              <h6>Last Three Month</h6>
            </div>
          </div>
          <div className="col-lg-9 col-md-9 col-sm-12 text-center  p-4">
            <div className="row">
              <h6>Turnover</h6>
              <hr />
            </div>

            {isLoading ? (
              <div className={styles.loaderImg}></div>
            ) : (
              <>
                <div className="row mb-1">
                  <div
                    className={`${styles.bgGreen} col-12 col-md-3 col-sm-12 text-center text-light border py-4`}
                  >
                    <h6>Completed</h6>

                    <span
                      key={breakDown.collection}
                      className="animate__animated animate__fadeIn"
                    >
                      {breakDown.collection.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`${styles.bgPurple} col-12 col-md-6 col-sm-12 text-center text-light border py-4`}
                  >
                    <h6>Grand Total</h6>

                    <span
                      key={breakDown.total}
                      className="animate__animated animate__fadeIn"
                    >
                      {breakDown.total.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`${styles.bgRed} col-12 col-md-3 col-sm-12 text-center text-light border py-4`}
                  >
                    <h6>Due</h6>
                    <span
                      key={breakDown.total - breakDown.collection}
                      className="animate__animated animate__flash"
                    >
                      {(breakDown.total - breakDown.collection).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="row">
                  <div
                    className={`${styles.bgOrange} col-12 col-md-4 col-sm-12 text-center text-light border py-4`}
                  >
                    <h6>Banking Total</h6>

                    <span
                      key={breakDown.bankingTotal}
                      className="animate__animated animate__fadeIn"
                    >
                      {breakDown.bankingTotal.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`${styles.bgPurpleLight} col-6 col-md-4 col-sm-6 text-center text-light border py-4`}
                  >
                    <h6>Banking Completed</h6>
                    <span
                      key={breakDown.bankingCollection}
                      className="animate__animated animate__fadeIn"
                    >
                      {breakDown.bankingCollection.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`${styles.bgPink} col-6 col-md-4 col-sm-6 text-center text-light border py-4`}
                  >
                    <h6>Banking Due</h6>

                    <span
                      key={breakDown.bankingTotal - breakDown.bankingCollection}
                      className="animate__animated animate__flash"
                    >
                      {(
                        breakDown.bankingTotal - breakDown.bankingCollection
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="row">
                  <div
                    className={`${styles.bgOrange} col-12 col-md-4 col-sm-12 text-center text-light border py-4`}
                  >
                    <h6>Total Cash</h6>
                    <span
                      key={breakDown.cashTotal}
                      className="animate__animated animate__fadeIn"
                    >
                      {breakDown.cashTotal.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`${styles.bgPurpleLight} col-6 col-md-4 col-sm-6 text-center text-light border py-4`}
                  >
                    <h6>Cash Completed</h6>
                    <span
                      key={breakDown.cashCollection}
                      className="animate__animated animate__fadeIn"
                    >
                      {breakDown.cashCollection.toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`${styles.bgPink} col-6 col-md-4 col-sm-6 text-center text-light border py-4 animated`}
                  >
                    <h6>Cash Due</h6>
                    <span
                      key={breakDown.cashTotal - breakDown.cashCollection}
                      className="animate__animated animate__flash"
                    >
                      {(breakDown.cashTotal - breakDown.cashCollection).toFixed(
                        2,
                      )}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      <SalesVsCollectionBarChart dataKey={chartDataKey} data={barChartData} />
    </div>
  );
};
